import get from 'lodash/get';
import { useEffect, useState } from 'react';
import POPOVER_POSITIONS from '../constants/POPOVER_POSITIONS';
import usePrevious from './usePrevious';
import type { IPopoverController } from '../lib/widget/popover/popover.interface';
import { useInjection } from 'inversify-react';
import { POPOVER_MODULE_IDS } from '../lib/widget/popover/popover.types';
import { getBoundingClientRect } from '../helpers/coordinates';

interface State {
  top: number;
  left: number;
  position: POPOVER_POSITIONS;
  title: string;
  text: string;
  isHidden: boolean;
  data: {
    offset?: number;
    x?: number;
    y?: number;
    title?: string;
    subtitle?: string;
    isCategory?: boolean;
    price?: string;
    prices?: string;
    text?: string;
    footer?: string;
    statusIcon?: any;
    isBrokersTicket?: boolean;
    ticketType?: number;
  };
}

let containerRef: HTMLElement;

const usePopover = () => {
  const popoverController: IPopoverController = useInjection(POPOVER_MODULE_IDS.POPOVER_CONTROLLER);

  const { Widget } = window;
  const [state, setState] = useState<State>({
    top: 0,
    left: 0,
    position: POPOVER_POSITIONS.DEFAULT,
    isHidden: true,
    title: null,
    text: null,
    data: {},
  });
  const prevState = usePrevious(state);

  useEffect(() => {
    popoverController.addListener(popoverController.events.UPDATE, update);
    popoverController.addListener(popoverController.events.MOVE, move);

    return () => {
      popoverController.removeListener(popoverController.events.UPDATE, update);
      popoverController.removeListener(popoverController.events.MOVE, move);
    };
  }, []);

  useEffect(() => {
    if (
      state.data &&
      ((!state.isHidden && get(prevState, 'data.x') !== get(state, 'data.x')) ||
        get(prevState, 'data.y') !== get(state, 'data.y'))
    ) {
      return updateContainerCoordinates();
    }
  }, [state]);

  const move = ({ x, y }) => {
    const { offsetTop, offsetLeft } = Widget;

    setState((prevState) => ({
      ...prevState,
      data: {
        ...prevState.data,
        x: x - offsetLeft,
        y: y - offsetTop,
      },
    }));

    updateContainerCoordinates();
  };

  const update = ({ isHidden, data }) => {
    setState((prevState) => ({
      ...prevState,
      isHidden,
      data,
    }));
  };

  const updateContainerCoordinates = () => {
    const { data } = state;
    const { offset, x, y } = data;

    if (x && y) {
      const { width, height } = getBoundingClientRect(containerRef);
      const { width: rootWidth } = Widget;
      let left = x - width / 2;
      let top = y - height - offset;

      let position = POPOVER_POSITIONS.DEFAULT;

      if (top < 0) {
        top = y + offset;
        left = x - width / 2;
        position = POPOVER_POSITIONS.BOTTOM;
      }

      if (left + width + offset > rootWidth) {
        top = y - height / 2;
        left = x - width - offset;
        position = POPOVER_POSITIONS.LEFT;
      }

      if (left < 0) {
        top = y - height / 2;
        left = x + offset;
        position = POPOVER_POSITIONS.RIGHT;
      }

      setState((prevState) => ({
        ...prevState,
        left,
        top,
        position,
      }));
    }
  };

  const getContainer = (node: HTMLDivElement) => {
    containerRef = node;
  };

  return { state, getContainer };
};

export default usePopover;
